import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Plus,
    Calendar,
    Clock,
    Video,
    Play,
    AlertCircle,
    Inbox,
    CheckCircle2,
    X,
    Save,
    FileVideo,
    Edit,
    Trash2,
    Globe,
    Users,
    Power,
    Check,
    BellRing,
    CalendarDays,
    Sparkles
} from 'lucide-react';
import { formatCalendarDate } from '../../utils/dateUtils';

interface Cohort {
    id: string;
    name: string;
    course_id?: number;
    course?: {
        id: number;
        title: string;
    };
}

interface Occurrence {
    date: string;
    day_name: string;
    formatted_date: string;
    start_time: string;
    end_time: string;
    formatted_time: string;
    formatted_range: string;
    timezone: string;
    timezone_display: string;
    status: 'upcoming' | 'starting_soon' | 'live' | 'ended';
    status_label: string;
    human_diff: string;
    start_iso: string;
    end_iso: string;
    is_live_now: boolean;
    is_starting_soon: boolean;
    can_join: boolean;
}

interface LiveSession {
    id: number;
    title: string;
    description?: string;
    course_id?: number;
    cohort_id?: string;
    course?: {
        id: number;
        title: string;
    };
    cohort?: {
        id: string;
        name: string;
    };
    scheduled_date?: string;
    schedule_start_date?: string;
    schedule_end_date?: string;
    start_time: string;
    end_time?: string;
    duration_minutes?: number;
    timezone?: string;
    meeting_link?: string;
    recording_link?: string;
    instructor_name?: string;
    is_recurring?: boolean;
    recurring_days?: string[];
    is_active?: boolean;
    formatted_schedule?: string;
    next_occurrence?: Occurrence;
    upcoming_occurrences?: Occurrence[];
    current_status?: string;
}

const WEEKDAYS = [
    { key: 'monday', label: 'Mon', full: 'Monday' },
    { key: 'tuesday', label: 'Tue', full: 'Tuesday' },
    { key: 'wednesday', label: 'Wed', full: 'Wednesday' },
    { key: 'thursday', label: 'Thu', full: 'Thursday' },
    { key: 'friday', label: 'Fri', full: 'Friday' },
    { key: 'saturday', label: 'Sat', full: 'Saturday' },
    { key: 'sunday', label: 'Sun', full: 'Sunday' },
];

const DURATION_OPTIONS = [
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
    { value: 150, label: '2.5 hours' },
    { value: 180, label: '3 hours' },
    { value: 240, label: '4 hours' },
];

export default function LiveClass() {
    const navigate = useNavigate();
    const location = useLocation();
    const [sessions, setSessions] = useState<LiveSession[]>([]);
    const [cohorts, setCohorts] = useState<Cohort[]>([]);
    const [courses, setCourses] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<"all" | "active" | "live" | "starting_soon" | "upcoming" | "disabled">("all");
    const [showSuccessToast, setShowSuccessToast] = useState(false);

    // Recording modal state
    const [recordingModal, setRecordingModal] = useState<{ show: boolean, sessionId: number | null }>({ show: false, sessionId: null });
    const [savingRecording, setSavingRecording] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadedUrl, setUploadedUrl] = useState('');

    // Edit modal state
    const [editModal, setEditModal] = useState<{ show: boolean, session: LiveSession | null }>({ show: false, session: null });
    const [updating, setUpdating] = useState(false);
    const [editForm, setEditForm] = useState({
        title: '',
        description: '',
        cohort_id: '',
        course_id: '',
        selectedDays: ['saturday'] as string[],
        start_time: '01:00',
        duration_minutes: 120,
        hasEndDate: false,
        schedule_start_date: '',
        schedule_end_date: '',
        meeting_link: '',
        recording_link: '',
        is_active: true,
    });

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    const fetchSessions = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/live-sessions`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (response.ok && Array.isArray(data)) {
                setSessions(data);
            }
        } catch (err) {
            console.error("Fetch Sessions Error:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMetadata = async () => {
        try {
            const token = localStorage.getItem('token');
            const [cohortsRes, coursesRes] = await Promise.all([
                fetch(`${API_URL}/cohorts`, {
                    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_URL}/courses`, {
                    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (cohortsRes.ok) {
                const cohortsData = await cohortsRes.json();
                if (Array.isArray(cohortsData)) setCohorts(cohortsData);
            }
            if (coursesRes.ok) {
                const coursesData = await coursesRes.json();
                if (Array.isArray(coursesData)) setCourses(coursesData);
            }
        } catch (err) {
            console.error("Fetch Metadata Error:", err);
        }
    };

    useEffect(() => {
        fetchSessions();
        fetchMetadata();
    }, [API_URL]);

    // Show success toast if redirected from creation page
    useEffect(() => {
        if (location.state?.success) {
            setShowSuccessToast(true);
            const timer = setTimeout(() => {
                setShowSuccessToast(false);
                window.history.replaceState({}, document.title);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [location]);

    // Refresh every minute to keep dynamic occurrence status accurate
    useEffect(() => {
        const timer = setInterval(() => {
            fetchSessions();
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    const getSessionComputedStatus = (session: LiveSession): string => {
        if (session.is_active === false) return 'disabled';
        if (session.next_occurrence?.status) return session.next_occurrence.status;
        return session.current_status || 'upcoming';
    };

    const filteredSessions = sessions.filter((session: LiveSession) => {
        const status = getSessionComputedStatus(session);
        if (filter === "all") return true;
        if (filter === "active") return session.is_active !== false;
        if (filter === "disabled") return session.is_active === false;
        return status === filter;
    });

    const getStatusBadge = (session: LiveSession) => {
        const status = getSessionComputedStatus(session);
        switch (status) {
            case 'live':
                return { bg: '#ecfdf5', border: '#a7f3d0', color: '#059669', label: 'LIVE NOW' };
            case 'starting_soon':
                return { bg: '#fffbeb', border: '#fde68a', color: '#d97706', label: 'STARTING SOON' };
            case 'upcoming':
                return { bg: '#eff6ff', border: '#bfdbfe', color: '#2563eb', label: 'UPCOMING' };
            case 'ended':
                return { bg: '#f1f5f9', border: '#cbd5e1', color: '#64748b', label: 'ENDED' };
            case 'disabled':
                return { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', label: 'DISABLED' };
            default:
                return { bg: '#f1f5f9', border: '#cbd5e1', color: '#64748b', label: status.toUpperCase() };
        }
    };

    const handleOpenEdit = (session: LiveSession) => {
        const days = Array.isArray(session.recurring_days) && session.recurring_days.length > 0
            ? session.recurring_days
            : ['saturday'];

        setEditModal({ show: true, session });
        setEditForm({
            title: session.title || '',
            description: session.description || '',
            cohort_id: session.cohort_id || '',
            course_id: session.course_id ? session.course_id.toString() : '',
            selectedDays: days,
            start_time: session.start_time ? session.start_time.substring(0, 5) : '01:00',
            duration_minutes: session.duration_minutes || 120,
            hasEndDate: !!session.schedule_end_date,
            schedule_start_date: session.schedule_start_date || (session.scheduled_date || ''),
            schedule_end_date: session.schedule_end_date || '',
            meeting_link: session.meeting_link || '',
            recording_link: session.recording_link || '',
            is_active: session.is_active !== false,
        });
        setUploadedUrl(session.recording_link || '');
    };

    const toggleEditDay = (dayKey: string) => {
        setEditForm(prev => {
            const exists = prev.selectedDays.includes(dayKey);
            let updatedDays: string[];
            if (exists) {
                if (prev.selectedDays.length === 1) return prev;
                updatedDays = prev.selectedDays.filter(d => d !== dayKey);
            } else {
                updatedDays = [...prev.selectedDays, dayKey];
            }
            return { ...prev, selectedDays: updatedDays };
        });
    };

    const handleUpdateSession = async () => {
        if (!editModal.session) return;
        setUpdating(true);
        try {
            const token = localStorage.getItem('token');
            const submitPayload: any = {
                title: editForm.title,
                description: editForm.description,
                cohort_id: editForm.cohort_id || null,
                course_id: editForm.course_id || null,
                recurring_days: editForm.selectedDays,
                start_time: editForm.start_time,
                duration_minutes: editForm.duration_minutes,
                schedule_start_date: editForm.schedule_start_date || null,
                schedule_end_date: editForm.hasEndDate && editForm.schedule_end_date ? editForm.schedule_end_date : null,
                meeting_link: editForm.meeting_link,
                recording_link: editForm.recording_link,
                is_active: editForm.is_active,
            };

            const response = await fetch(`${API_URL}/live-sessions/${editModal.session.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(submitPayload)
            });

            if (response.ok) {
                await fetchSessions();
                setEditModal({ show: false, session: null });
            } else {
                const data = await response.json();
                alert(data.message || 'Update failed');
            }
        } catch (err) {
            console.error("Update Session Error:", err);
            alert('A network error occurred.');
        } finally {
            setUpdating(false);
        }
    };

    const handleToggleActive = async (session: LiveSession) => {
        const nextState = !session.is_active;
        const confirmMsg = nextState
            ? `Enable this recurring Live Session? Future sessions will start appearing on student schedules and reminders will resume.`
            : `Disable this recurring Live Session? Future sessions will no longer appear to students and reminders will stop. Past sessions will remain intact in history.`;

        if (!window.confirm(confirmMsg)) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/live-sessions/${session.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ is_active: nextState })
            });

            if (response.ok) {
                await fetchSessions();
            } else {
                alert('Failed to update schedule status.');
            }
        } catch (err) {
            alert('A network error occurred.');
        }
    };

    const handleDeleteSession = async (id: number) => {
        if (!window.confirm('Are you sure you want to permanently delete this live session schedule?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/live-sessions/${id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                setSessions(sessions.filter(s => s.id !== id));
            } else {
                alert('Deletion failed. Retry later.');
            }
        } catch (err) {
            alert('A network error occurred.');
        }
    };

    const handleOpenRecordingModal = (session: LiveSession) => {
        setRecordingModal({
            show: true,
            sessionId: session.id,
        });
        setUploadedUrl(session.recording_link || '');
        setUploadProgress(0);
        setUploading(false);
    };

    const handleFileUpload = async (file: File) => {
        const token = localStorage.getItem('token');
        setUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('video', file);

        try {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', `${API_URL}/upload-video`, true);
            xhr.setRequestHeader('Accept', 'application/json');
            if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress(Math.round((event.loaded / event.total) * 100));
                }
            };

            const response: any = await new Promise((resolve, reject) => {
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            resolve(JSON.parse(xhr.responseText));
                        } catch { reject(new Error('Invalid response')); }
                    } else {
                        reject(new Error('Upload failed'));
                    }
                };
                xhr.onerror = () => reject(new Error('Network error'));
                xhr.send(formData);
            });

            if (response.success) {
                setUploadedUrl(response.video_url);
                if (editModal.show) {
                    setEditForm(prev => ({ ...prev, recording_link: response.video_url }));
                }
            } else {
                alert(response.message || 'Upload failed');
            }
        } catch (err: any) {
            alert(err.message || 'An error occurred during upload');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteVideo = async () => {
        if (!window.confirm('Delete this recording permanently?')) return;
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${API_URL}/delete-video`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ video_url: uploadedUrl })
            });

            if (response.ok) {
                setUploadedUrl('');
                if (editModal.show) {
                    setEditForm(prev => ({ ...prev, recording_link: '' }));
                }
            } else {
                alert('Failed to delete video');
            }
        } catch (err) {
            alert('An error occurred during deletion');
        }
    };

    const handleSaveRecording = async () => {
        if (!recordingModal.sessionId || !uploadedUrl) return;
        setSavingRecording(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/live-sessions/${recordingModal.sessionId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    recording_link: uploadedUrl
                })
            });

            if (response.ok) {
                await fetchSessions();
                setRecordingModal({ show: false, sessionId: null });
                setUploadedUrl('');
            } else {
                const data = await response.json();
                alert(data.message || 'Failed to save recording link');
            }
        } catch (err) {
            console.error("Save Recording Error:", err);
            alert('A network error occurred.');
        } finally {
            setSavingRecording(false);
        }
    };

    // Edit modal summary preview
    const editScheduleSummary = useMemo(() => {
        if (editForm.selectedDays.length === 0) return 'No days selected';
        const formattedDays = editForm.selectedDays.map(d => {
            const match = WEEKDAYS.find(w => w.key === d);
            return match ? match.full : d;
        });

        let daysText = '';
        if (formattedDays.length === 1) daysText = `Every ${formattedDays[0]}`;
        else if (formattedDays.length === 2) daysText = `Every ${formattedDays[0]} & ${formattedDays[1]}`;
        else {
            const last = formattedDays[formattedDays.length - 1];
            const rest = formattedDays.slice(0, -1);
            daysText = `Every ${rest.join(', ')} & ${last}`;
        }

        let timeFormatted = editForm.start_time;
        if (editForm.start_time) {
            const [hours, minutes] = editForm.start_time.split(':').map(Number);
            const period = hours >= 12 ? 'PM' : 'AM';
            const adjustedHours = hours % 12 || 12;
            timeFormatted = `${adjustedHours}:${minutes < 10 ? '0' + minutes : minutes} ${period}`;
        }

        const durationMatch = DURATION_OPTIONS.find(d => d.value === Number(editForm.duration_minutes));
        const durationText = durationMatch ? durationMatch.label : `${editForm.duration_minutes} mins`;

        return `${daysText} at ${timeFormatted} Central Time (${durationText})`;
    }, [editForm.selectedDays, editForm.start_time, editForm.duration_minutes]);

    return (
        <div className="animate-fade-in-up" style={{ paddingBottom: '4rem' }}>
            <style>{`
                .staff-scope .live-header-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 2rem;
                    gap: 1.5rem;
                    flex-wrap: wrap;
                }

                .staff-scope .filter-tabs {
                    display: flex;
                    gap: 0.5rem;
                    background: var(--index-hover-bg);
                    padding: 0.4rem;
                    border-radius: 14px;
                    width: fit-content;
                    max-width: 100%;
                    overflow-x: auto;
                    scrollbar-width: none;
                }

                .staff-scope .filter-tabs::-webkit-scrollbar {
                    display: none;
                }

                .staff-scope .filter-btn {
                    padding: 0.6rem 1.2rem;
                    border: none;
                    background: transparent;
                    color: var(--index-text-secondary);
                    font-weight: 700;
                    font-size: 0.85rem;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }

                .staff-scope .filter-btn.active {
                    background: white;
                    color: #0F5132;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                }

                .staff-scope .sessions-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
                    gap: 1.5rem;
                }

                .staff-scope .live-session-card {
                    background: white;
                    border: 1px solid var(--index-border-color);
                    border-radius: 20px;
                    padding: 1.75rem;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex;
                    flex-direction: column;
                    position: relative;
                    overflow: hidden;
                }

                .staff-scope .live-session-card:hover {
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.06);
                    border-color: #cbd5e1;
                    transform: translateY(-3px);
                }

                .staff-scope .session-status-badge {
                    padding: 0.35rem 0.75rem;
                    border-radius: 8px;
                    font-size: 0.72rem;
                    font-weight: 800;
                    letter-spacing: 0.05em;
                    width: fit-content;
                    border: 1px solid;
                }

                .staff-scope .next-session-box {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 1rem 1.2rem;
                    margin: 1.25rem 0;
                }

                .staff-scope .btn-go-live {
                    width: 100%;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.65rem;
                    background: #0F5132;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-weight: 700;
                    font-size: 0.95rem;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-decoration: none;
                }

                .btn-go-live:hover:not(:disabled) {
                    background: #064e3b;
                    box-shadow: 0 10px 15px -3px rgba(15, 81, 50, 0.25);
                }

                .btn-go-live:disabled {
                    background: var(--index-hover-bg);
                    color: var(--index-text-faint);
                    cursor: not-allowed;
                }

                .staff-scope .empty-state-container {
                    padding: 6rem 2rem;
                    text-align: center;
                    background: white;
                    border: 2px dashed var(--index-border-color);
                    border-radius: 28px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .staff-scope .modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(15, 23, 42, 0.5);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2000;
                    padding: 1.5rem;
                    overflow-y: auto;
                }

                .staff-scope .recording-modal {
                    background: white;
                    width: 100%;
                    max-width: 620px;
                    border-radius: 24px;
                    padding: 2.25rem;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    max-height: 90vh;
                    overflow-y: auto;
                }

                .staff-scope .tz-banner {
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    border-radius: 12px;
                    padding: 0.75rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.65rem;
                    margin-bottom: 2rem;
                    font-size: 0.85rem;
                    color: #166534;
                    font-weight: 600;
                }

                .staff-scope .cohort-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    background: #f1f5f9;
                    color: #334155;
                    padding: 4px 10px;
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 700;
                }

                .staff-scope .weekday-pill-grid {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: 0.4rem;
                }

                .staff-scope .weekday-btn {
                    padding: 0.6rem 0.3rem;
                    border-radius: 10px;
                    border: 2px solid var(--index-border-color);
                    background: white;
                    font-weight: 700;
                    font-size: 0.8rem;
                    cursor: pointer;
                    text-align: center;
                    transition: all 0.2s;
                    color: var(--index-text-secondary);
                }

                .staff-scope .weekday-btn.selected {
                    background: #0F5132;
                    border-color: #0F5132;
                    color: white;
                }

                .staff-scope .success-toast {
                    position: fixed;
                    bottom: 2rem;
                    right: 2rem;
                    background: #0F5132;
                    color: white;
                    padding: 1rem 1.5rem;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    box-shadow: 0 10px 20px rgba(15, 81, 50, 0.25);
                    z-index: 3000;
                }

                @media (max-width: 640px) {
                    .staff-scope .sessions-grid {
                        grid-template-columns: 1fr;
                    }
                    .staff-scope .weekday-pill-grid {
                        grid-template-columns: repeat(4, 1fr);
                    }
                }
            `}</style>

            {/* Header section */}
            <div className="live-header-section">
                <div>
                    <h1 className="dashboard-header-title" style={{ margin: 0, fontSize: '1.85rem', fontWeight: 800 }}>Live Sessions</h1>
                    <p style={{ color: 'var(--index-text-secondary)', marginTop: '0.35rem', fontSize: '0.95rem' }}>
                        Manage recurring class schedules and host live cohort sessions.
                    </p>
                </div>

                <button
                    className="btn-standard"
                    style={{ background: '#0F5132', color: 'white', padding: '0.85rem 1.6rem', borderRadius: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}
                    onClick={() => navigate('/instructor/live/create')}
                >
                    <Plus size={18} /> + Create Live Session
                </button>
            </div>

            {/* Authoritative Timezone Banner */}
            <div className="tz-banner">
                <Globe size={18} style={{ color: '#0F5132', flexShrink: 0 }} />
                <div>
                    All Live Sessions are scheduled in <strong>Central Time (America/Chicago)</strong>. Automated 30-minute email reminders are dispatched dynamically before each occurrence.
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="filter-tabs" style={{ marginBottom: '2rem' }}>
                {[
                    { key: 'all', label: 'All Schedules' },
                    { key: 'active', label: 'Active' },
                    { key: 'live', label: '🔴 Live Now' },
                    { key: 'starting_soon', label: '⚡ Starting Soon' },
                    { key: 'upcoming', label: 'Upcoming' },
                    { key: 'disabled', label: 'Disabled' },
                ].map(tab => (
                    <button
                        key={tab.key}
                        className={`filter-btn ${filter === tab.key ? 'active' : ''}`}
                        onClick={() => setFilter(tab.key as any)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Sessions Grid */}
            {loading ? (
                <div style={{ padding: '6rem 2rem', textAlign: 'center', color: 'var(--index-text-secondary)', fontWeight: 600 }}>
                    Loading live class schedules...
                </div>
            ) : filteredSessions.length > 0 ? (
                <div className="sessions-grid">
                    {filteredSessions.map(session => {
                        const badge = getStatusBadge(session);
                        const next = session.next_occurrence;
                        const hasMeetingLink = !!session.meeting_link;
                        const isLive = next?.is_live_now || session.current_status === 'live';
                        const isStartingSoon = next?.is_starting_soon || session.current_status === 'starting_soon';
                        const isDisabled = session.is_active === false;

                        return (
                            <div key={session.id} className="live-session-card" style={{ opacity: isDisabled ? 0.75 : 1 }}>
                                {/* Card Header: Status & Actions */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
                                    <div className="session-status-badge" style={{ background: badge.bg, borderColor: badge.border, color: badge.color }}>
                                        {badge.label}
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        {/* Toggle Active / Disable */}
                                        <button
                                            title={isDisabled ? "Enable Schedule" : "Disable Schedule"}
                                            onClick={() => handleToggleActive(session)}
                                            style={{
                                                background: isDisabled ? '#fef2f2' : '#f1f5f9',
                                                border: `1px solid ${isDisabled ? '#fecaca' : '#e2e8f0'}`,
                                                color: isDisabled ? '#dc2626' : '#64748b',
                                                padding: '0.45rem',
                                                borderRadius: '8px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <Power size={15} />
                                        </button>
                                        {/* Edit */}
                                        <button
                                            title="Edit Schedule"
                                            onClick={() => handleOpenEdit(session)}
                                            style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569', padding: '0.45rem', borderRadius: '8px', cursor: 'pointer' }}
                                        >
                                            <Edit size={15} />
                                        </button>
                                        {/* Delete */}
                                        <button
                                            title="Delete Session"
                                            onClick={() => handleDeleteSession(session.id)}
                                            style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', padding: '0.45rem', borderRadius: '8px', cursor: 'pointer' }}
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </div>

                                {/* Title & Cohort / Course */}
                                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--index-text-heading)', margin: '0 0 0.5rem 0', lineHeight: 1.3 }}>
                                    {session.title}
                                </h3>

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '1rem' }}>
                                    {session.cohort && (
                                        <span className="cohort-tag">
                                            <Users size={13} /> {session.cohort.name}
                                        </span>
                                    )}
                                    {session.course && (
                                        <span className="cohort-tag" style={{ background: '#ecfdf5', color: '#065f46' }}>
                                            <Video size={13} /> {session.course.title}
                                        </span>
                                    )}
                                </div>

                                {/* Recurring Schedule Pattern */}
                                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F5132', marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Clock size={16} /> {session.formatted_schedule || 'Recurring weekly class'}
                                </div>

                                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>
                                    Timezone: <strong>Central Time (America/Chicago)</strong>
                                </div>

                                {session.schedule_start_date && (
                                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.75rem' }}>
                                        Validity: {formatCalendarDate(session.schedule_start_date)}
                                        {session.schedule_end_date ? ` – ${formatCalendarDate(session.schedule_end_date)}` : ' – Ongoing'}
                                    </div>
                                )}

                                {/* Next Occurrence Box */}
                                <div className="next-session-box">
                                    <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                                        Next Session
                                    </div>
                                    {isDisabled ? (
                                        <div style={{ color: '#dc2626', fontWeight: 700, fontSize: '0.9rem' }}>
                                            Schedule is currently disabled
                                        </div>
                                    ) : next ? (
                                        <div>
                                            <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
                                                {next.formatted_date}
                                            </div>
                                            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#059669', marginTop: '2px' }}>
                                                {next.formatted_range || next.formatted_time}
                                            </div>
                                            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px' }}>
                                                {next.human_diff}
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                            No upcoming occurrences in schedule window.
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons Footer */}
                                <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                    {!hasMeetingLink && !isDisabled && (
                                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#b45309', padding: '0.5rem 0.75rem', borderRadius: '8px', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <AlertCircle size={14} /> No meeting URL added yet
                                        </div>
                                    )}

                                    {/* Join / Go Live Button */}
                                    {hasMeetingLink && !isDisabled ? (
                                        <a
                                            href={session.meeting_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-go-live"
                                            style={{
                                                background: (isLive || isStartingSoon) ? '#0F5132' : 'var(--index-hover-bg)',
                                                color: (isLive || isStartingSoon) ? 'white' : '#334155',
                                            }}
                                        >
                                            <Play size={16} fill="currentColor" />
                                            {isLive ? 'Host Live Class Now' : (isStartingSoon ? 'Class Starting Soon — Join' : 'Join / Open Meeting Link')}
                                        </a>
                                    ) : (
                                        <button className="btn-go-live" disabled>
                                            {isDisabled ? 'Schedule Disabled' : 'No Meeting Link'}
                                        </button>
                                    )}

                                    {/* Recording Button */}
                                    <button
                                        type="button"
                                        onClick={() => handleOpenRecordingModal(session)}
                                        style={{
                                            background: session.recording_link ? '#f0fdf4' : '#f8fafc',
                                            border: `1px solid ${session.recording_link ? '#bbf7d0' : '#e2e8f0'}`,
                                            color: session.recording_link ? '#065f46' : '#64748b',
                                            padding: '0.65rem',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '0.82rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <FileVideo size={15} />
                                        {session.recording_link ? 'Manage Class Recording' : '+ Add Recording'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="empty-state-container">
                    <div style={{ padding: '2rem', background: '#f8fafc', borderRadius: '32px', marginBottom: '1.5rem' }}>
                        <Inbox size={56} color="#94a3b8" strokeWidth={1.5} />
                    </div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--index-text-heading)', margin: '0 0 0.5rem 0' }}>
                        No live session schedules found
                    </h2>
                    <p style={{ color: 'var(--index-text-secondary)', maxWidth: '380px', margin: '0 0 1.5rem 0', fontSize: '0.9rem' }}>
                        Create a recurring weekly live class schedule. You won't have to manually update dates each week!
                    </p>
                    <button
                        className="btn-standard"
                        style={{ background: '#0F5132', color: 'white', padding: '0.8rem 1.6rem', borderRadius: '12px', fontWeight: 700 }}
                        onClick={() => navigate('/instructor/live/create')}
                    >
                        + Create First Live Schedule
                    </button>
                </div>
            )}

            {/* Success Toast */}
            {showSuccessToast && (
                <div className="success-toast">
                    <CheckCircle2 size={20} />
                    <span>Live session schedule saved successfully!</span>
                </div>
            )}

            {/* Recording Upload Modal */}
            {recordingModal.show && (
                <div className="modal-overlay">
                    <div className="recording-modal" style={{ maxWidth: '480px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, margin: 0 }}>Class Recording</h3>
                            <button
                                onClick={() => setRecordingModal({ show: false, sessionId: null })}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                            Upload or update the recorded video file for this live class for students to access.
                        </p>

                        <div style={{ marginBottom: '1.5rem' }}>
                            {uploading ? (
                                <div style={{ padding: '1.5rem', background: '#f8fafc', borderRadius: '14px', textAlign: 'center' }}>
                                    <div style={{ fontWeight: 700, marginBottom: '0.5rem', fontSize: '0.85rem' }}>Uploading... {uploadProgress}%</div>
                                    <div style={{ width: '100%', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                        <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#0F5132', transition: 'width 0.3s' }}></div>
                                    </div>
                                </div>
                            ) : uploadedUrl ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#ecfdf5', padding: '0.85rem', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                                    <CheckCircle2 size={18} color="#059669" />
                                    <div style={{ flex: 1, fontSize: '0.78rem', fontWeight: 600, color: '#065f46', wordBreak: 'break-all' }}>Recording Uploaded</div>
                                    <button type="button" onClick={handleDeleteVideo} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 800 }}>Delete</button>
                                </div>
                            ) : (
                                <input
                                    type="file"
                                    accept="video/*"
                                    onChange={(e) => {
                                        if (e.target.files?.[0]) handleFileUpload(e.target.files[0]);
                                        e.target.value = '';
                                    }}
                                    style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                                />
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                className="btn-standard"
                                style={{ flex: 1, background: '#f1f5f9', color: '#475569', justifyContent: 'center' }}
                                onClick={() => setRecordingModal({ show: false, sessionId: null })}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-standard"
                                style={{ flex: 2, background: '#0F5132', color: 'white', justifyContent: 'center' }}
                                onClick={handleSaveRecording}
                                disabled={savingRecording || !uploadedUrl}
                            >
                                {savingRecording ? 'Saving...' : <><Save size={16} /> Save Recording</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Schedule Modal */}
            {editModal.show && (
                <div className="modal-overlay">
                    <div className="recording-modal">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <div>
                                <h3 style={{ fontSize: '1.3rem', fontWeight: 800, margin: 0 }}>Edit Recurring Schedule</h3>
                                <div style={{ fontSize: '0.8rem', color: '#0284c7', fontWeight: 600, marginTop: '2px' }}>
                                    Changes will apply to all future occurrences. Past sessions remain in history.
                                </div>
                            </div>
                            <button
                                onClick={() => setEditModal({ show: false, session: null })}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                            >
                                <X size={22} />
                            </button>
                        </div>

                        {/* Preview summary banner */}
                        <div style={{ background: 'linear-gradient(135deg, #0F5132 0%, #064e3b 100%)', borderRadius: '14px', padding: '1rem 1.25rem', color: 'white', marginBottom: '1.5rem' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#86efac', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Sparkles size={14} /> Schedule Summary Preview
                            </div>
                            <div style={{ fontSize: '1rem', fontWeight: 800, marginTop: '4px' }}>
                                {editScheduleSummary}
                            </div>
                        </div>

                        {/* Form Fields */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem' }}>Session Title</label>
                                <input
                                    type="text"
                                    className="form-input-modern"
                                    value={editForm.title}
                                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem' }}>Cohort</label>
                                    <select
                                        value={editForm.cohort_id}
                                        onChange={(e) => setEditForm({ ...editForm, cohort_id: e.target.value })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                                    >
                                        <option value="">Select Cohort</option>
                                        {cohorts.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem' }}>Course</label>
                                    <select
                                        value={editForm.course_id}
                                        onChange={(e) => setEditForm({ ...editForm, course_id: e.target.value })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                                    >
                                        <option value="">Select Course</option>
                                        {courses.map(c => (
                                            <option key={c.id} value={c.id}>{c.title}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Recurring Days */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem' }}>Recurring Days</label>
                                <div className="weekday-pill-grid">
                                    {WEEKDAYS.map(day => {
                                        const isSelected = editForm.selectedDays.includes(day.key);
                                        return (
                                            <button
                                                type="button"
                                                key={day.key}
                                                className={`weekday-btn ${isSelected ? 'selected' : ''}`}
                                                onClick={() => toggleEditDay(day.key)}
                                            >
                                                {isSelected && <Check size={12} style={{ display: 'inline', marginRight: '2px' }} />}
                                                {day.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Start Time & Duration */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem' }}>
                                        Start Time (Central Time)
                                    </label>
                                    <input
                                        type="time"
                                        value={editForm.start_time}
                                        onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                                    />
                                    <span style={{ fontSize: '0.72rem', color: '#64748b' }}>America/Chicago</span>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem' }}>Duration</label>
                                    <select
                                        value={editForm.duration_minutes}
                                        onChange={(e) => setEditForm({ ...editForm, duration_minutes: Number(e.target.value) })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                                    >
                                        {DURATION_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Dates */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem' }}>Schedule Starts</label>
                                    <input
                                        type="date"
                                        value={editForm.schedule_start_date}
                                        onChange={(e) => setEditForm({ ...editForm, schedule_start_date: e.target.value })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>

                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                                        <label style={{ fontSize: '0.85rem', fontWeight: 700, margin: 0 }}>Schedule Ends</label>
                                        <label style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={editForm.hasEndDate}
                                                onChange={(e) => setEditForm({ ...editForm, hasEndDate: e.target.checked })}
                                            />
                                            Has end date
                                        </label>
                                    </div>
                                    <input
                                        type="date"
                                        disabled={!editForm.hasEndDate}
                                        value={editForm.schedule_end_date}
                                        onChange={(e) => setEditForm({ ...editForm, schedule_end_date: e.target.value })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1', opacity: editForm.hasEndDate ? 1 : 0.5 }}
                                    />
                                </div>
                            </div>

                            {/* Meeting Link */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem' }}>Meeting URL</label>
                                <input
                                    type="url"
                                    placeholder="https://teams.microsoft.com/... or https://zoom.us/..."
                                    value={editForm.meeting_link}
                                    onChange={(e) => setEditForm({ ...editForm, meeting_link: e.target.value })}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                                />
                            </div>

                            {/* Status */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: '10px' }}>
                                <input
                                    type="checkbox"
                                    id="edit_is_active"
                                    checked={editForm.is_active}
                                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                    style={{ width: '18px', height: '18px' }}
                                />
                                <label htmlFor="edit_is_active" style={{ fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer', margin: 0 }}>
                                    Active (Schedule enabled and upcoming classes visible)
                                </label>
                            </div>
                        </div>

                        {/* Modal Action Buttons */}
                        <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
                            <button
                                className="btn-standard"
                                style={{ flex: 1, background: '#f1f5f9', color: '#475569', justifyContent: 'center' }}
                                onClick={() => setEditModal({ show: false, session: null })}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-standard"
                                style={{ flex: 2, background: '#0F5132', color: 'white', justifyContent: 'center' }}
                                onClick={handleUpdateSession}
                                disabled={updating}
                            >
                                {updating ? 'Saving Schedule...' : 'Save & Update Schedule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
